/**
 * Campaign Lists Hooks
 * React Query hooks for campaign list operations
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getCampaignLists, getCampaignListDetails, createCampaignList, updateCampaignList, deleteCampaignList, addListMembers, updateMemberStatus, removeListMembers } from '@lingaai/supabase-client';

/**
 * Hook to get all campaign lists
 */
export function useCampaignLists() {
  return useQuery({
    queryKey: ['campaign-lists'],
    queryFn: getCampaignLists,
  });
}

/**
 * Hook to get a single campaign list with members
 */
export function useCampaignListDetails(id: string | undefined) {
  return useQuery({
    queryKey: ['campaign-list', id],
    queryFn: () => getCampaignListDetails(id!),
    enabled: !!id,
  });
}

/**
 * Hook to create a campaign list
 */
export function useCreateCampaignList() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createCampaignList,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaign-lists'] });
    },
  });
}

/**
 * Hook to update a campaign list
 */
export function useUpdateCampaignList() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof updateCampaignList>[1] }) => updateCampaignList(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['campaign-lists'] });
      queryClient.invalidateQueries({ queryKey: ['campaign-list', variables.id] });
    },
  });
}

/**
 * Hook to delete a campaign list
 */
export function useDeleteCampaignList() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteCampaignList,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaign-lists'] });
    },
  });
}

/**
 * Hook to add members to a list
 */
export function useAddListMembers() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ listId, nodeIds, status }: { listId: string; nodeIds: string[]; status?: 'suggested' | 'active' }) => addListMembers(listId, nodeIds, status),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['campaign-lists'] });
      queryClient.invalidateQueries({ queryKey: ['campaign-list', variables.listId] });
    },
  });
}

/**
 * Hook to update member status
 */
export function useUpdateMemberStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ listId, nodeIds, status, removalReason }: { listId: string; nodeIds: string[]; status: 'suggested' | 'active' | 'removed'; removalReason?: string }) =>
      updateMemberStatus(listId, nodeIds, status, removalReason),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['campaign-lists'] });
      queryClient.invalidateQueries({ queryKey: ['campaign-list', variables.listId] });
    },
  });
}

/**
 * Hook to remove members from a list
 */
export function useRemoveListMembers() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ listId, nodeIds }: { listId: string; nodeIds: string[] }) => removeListMembers(listId, nodeIds),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['campaign-lists'] });
      queryClient.invalidateQueries({ queryKey: ['campaign-list', variables.listId] });
    },
  });
}
